import { businessDaysAgo, businessToday } from "./businessDay";

/**
 * G-143 — the periods the web-orders report offers, as the whole Barcelona
 * days the server counts in (OD-221). "This month" is also what the server
 * answers when no period is sent.
 */
export type ReportPeriodPreset = "THIS_MONTH" | "LAST_MONTH" | "LAST_7_DAYS" | "CUSTOM";

export interface ReportDays {
  from: string;
  to: string;
}

const ymd = (year: number, month: number, day: number) =>
  new Date(Date.UTC(year, month - 1, day)).toISOString().slice(0, 10);

export function presetDays(preset: Exclude<ReportPeriodPreset, "CUSTOM">, now: Date = new Date()): ReportDays {
  const { year, month } = businessToday(now);
  switch (preset) {
    case "THIS_MONTH":
      // Day 0 of next month is this month's last day.
      return { from: ymd(year, month, 1), to: ymd(year, month + 1, 0) };
    case "LAST_MONTH":
      return { from: ymd(year, month - 1, 1), to: ymd(year, month, 0) };
    case "LAST_7_DAYS":
      return { from: businessDaysAgo(6, now), to: businessDaysAgo(0, now) };
  }
}

/** The longest period the server answers for (MAX_REPORT_DAYS on the server). */
export const MAX_REPORT_DAYS = 366;

/** Calendar days from `from` to `to`, both included. */
export function rangeDays(days: ReportDays): number {
  return (Date.parse(`${days.to}T00:00:00Z`) - Date.parse(`${days.from}T00:00:00Z`)) / 86_400_000 + 1;
}

/** A custom range is sent only when both days are there, in order, and at most a year long. */
export function isValidRange(days: ReportDays): boolean {
  return (
    /^\d{4}-\d{2}-\d{2}$/.test(days.from) &&
    /^\d{4}-\d{2}-\d{2}$/.test(days.to) &&
    days.from <= days.to &&
    rangeDays(days) <= MAX_REPORT_DAYS
  );
}

/** The preset a range is, so a link carrying days lands on the matching button. */
export function presetFor(days: ReportDays, now: Date = new Date()): ReportPeriodPreset {
  const presets: Exclude<ReportPeriodPreset, "CUSTOM">[] = ["THIS_MONTH", "LAST_MONTH", "LAST_7_DAYS"];
  return (
    presets.find((p) => {
      const d = presetDays(p, now);
      return d.from === days.from && d.to === days.to;
    }) ?? "CUSTOM"
  );
}

export interface ReportPeriodValue {
  preset: ReportPeriodPreset;
  /** The custom range as typed; used only when `preset` is CUSTOM. */
  custom: ReportDays;
}

/** A period from a link's `from`/`to`, else this month. */
export function periodFromParams(params: URLSearchParams): ReportPeriodValue {
  const days = { from: params.get("from") ?? "", to: params.get("to") ?? "" };
  if (!isValidRange(days)) return { preset: "THIS_MONTH", custom: presetDays("THIS_MONTH") };
  return { preset: presetFor(days), custom: days };
}

/** The days a period asks the server for; null while a custom range is incomplete. */
export function periodDays(value: ReportPeriodValue): ReportDays | null {
  if (value.preset !== "CUSTOM") return presetDays(value.preset);
  return isValidRange(value.custom) ? value.custom : null;
}
